import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import {
  usePendingAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from "../../hooks/useAttachments";
import { renderPdfFirstPageToWebp } from "../../utils/pdfToWebp";
import { useAddInvoice, useInvoices, useUpdateInvoice } from "../../api/lists";
import { useConfig } from "../../api/queries";
import { AttachmentUploadModal } from "../../components/AttachmentUploadModal";
import { InvoiceFormDialog } from "../../components/Settings/InvoiceFormDialog";
import { EntityPickerModal } from "../../components/EntityPickerField";
import { NewImportModal } from "../Admin/ImportsPage/components/NewImportModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import type {
  AttachmentDto,
  AttachmentId,
  ImportResultDto,
  InvoiceId,
  NewInvoiceEntity,
} from "../../api/types";
import { PendingAttachmentCard } from "./PendingAttachmentCard";
import { TransactionPickerDialog } from "./TransactionPickerDialog";
import { AttachmentManageDialog } from "../../components/AttachmentManageDialog";
import { PendingTransactionsSummary } from "./PendingTransactionsSummary";
import { InvoiceAmountWarnings } from "./InvoiceAmountWarnings";
import { RuleHealthWarnings } from "./RuleHealthWarnings";
import { MatchProblemWarnings } from "./MatchProblemWarnings";
import { StatementInvoiceWarnings } from "./StatementInvoiceWarnings";
import { ImportsNeedingRerun } from "./ImportsNeedingRerun";
import { PENDING_FILTER_NAME } from "../../logic/dashboard";
import { importOutcomeMessage } from "../../logic/import-outcome";

type ActionKind =
  | { kind: "attachToInvoice"; attachment: AttachmentDto }
  | { kind: "attachToTransaction"; attachment: AttachmentDto }
  | { kind: "edit"; attachment: AttachmentDto }
  | { kind: "delete"; attachment: AttachmentDto };

export const HomePage = () => {
  const { data: pending, isLoading } = usePendingAttachments();
  const { data: config } = useConfig();
  const addInvoice = useAddInvoice();
  const updateInvoice = useUpdateInvoice();
  const deleteAttachment = useDeleteAttachment();
  const uploadAttachment = useUploadAttachment();
  // The full invoices, not the registry's copy: the update below is a full
  // replace, and the registry leaves out the amounts, KID and pay-to account.
  const { refetch: refetchInvoices } = useInvoices();

  const pendingFilter = config?.predefinedFilters.find(
    (f) => f.name === PENDING_FILTER_NAME,
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  // Bumped on every open so the import dialog remounts with a blank form
  // rather than the previous upload's file and success alert.
  const [importOpenedAt, setImportOpenedAt] = useState<number | null>(null);
  // What the last import did: the dialog closes as soon as it succeeds, so its
  // own success alert is never seen from here.
  const [importOutcome, setImportOutcome] = useState<string | null>(null);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [action, setAction] = useState<ActionKind | null>(null);
  // A failed card action (delete) — shown on the page, since the action's
  // confirm dialog has closed by the time the server answers.
  const [actionError, setActionError] = useState<string | undefined>();

  // "Invoice from PDF": open the invoice form with the attachment prefilled and
  // detection running automatically. Entered either by uploading a freshly
  // picked PDF (the toolbar button) or by picking an already-pending one (the
  // per-card button).
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | undefined>();
  const [pdfInvoiceAttachmentId, setPdfInvoiceAttachmentId] =
    useState<AttachmentId | null>(null);

  const handlePdfPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setScanning(true);
    setScanError(undefined);
    try {
      const preview = await renderPdfFirstPageToWebp(file);
      const created = await uploadAttachment.mutateAsync({
        file,
        filename: file.name,
        contentType: "application/pdf",
        preview,
      });
      setPdfInvoiceAttachmentId(created.id as AttachmentId);
    } catch (err) {
      setScanError(
        err instanceof Error ? err.message : "Failed to read the PDF",
      );
    } finally {
      setScanning(false);
    }
  };

  const close = () => {
    setAction(null);
    setActionError(undefined);
  };

  // Throws on failure: the form shows the error itself, and turns a duplicate
  // refusal into its open / discard / create-anyway dialog.
  const handleCreateInvoice = async (
    entity: NewInvoiceEntity,
    { force }: { force: boolean },
  ) => {
    await addInvoice.mutateAsync({ invoice: entity, force });
    setNewInvoiceOpen(false);
    setPdfInvoiceAttachmentId(null);
    close();
  };

  // Only a direct save reports counts; a merge into an existing import was
  // already reviewed (or discarded) in its diff dialog, so it says nothing here.
  const showImportOutcome = (result?: ImportResultDto) => {
    if (result) setImportOutcome(importOutcomeMessage(result));
  };

  const handleImported = (result?: ImportResultDto) => {
    setImportOpenedAt(null);
    showImportOutcome(result);
  };

  const handleAttachToInvoice = async (invoiceId: InvoiceId | null) => {
    if (action?.kind !== "attachToInvoice" || invoiceId === null) return;
    const attachmentId = action.attachment.id as AttachmentId;
    try {
      // Fresh from the server: the PUT replaces every field, so it must start
      // from the stored invoice — and one just created in the picker is not in
      // the cache yet.
      const { data: invoices } = await refetchInvoices({ throwOnError: true });
      const invoice = invoices?.find((inv) => inv.id === invoiceId);
      if (!invoice) throw new Error("That invoice no longer exists");
      const existing = invoice.attachments ?? [];
      if (!existing.includes(attachmentId)) {
        await updateInvoice.mutateAsync({
          id: invoice.id,
          name: invoice.name,
          description: invoice.description,
          sourceId: invoice.sourceId,
          payeeId: invoice.payeeId,
          dateReceived: invoice.dateReceived,
          dueDate: invoice.dueDate,
          amount: invoice.amount,
          amountMax: invoice.amountMax,
          kid: invoice.kid,
          payToAccount: invoice.payToAccount,
          attachments: [...existing, attachmentId],
        });
      }
      close();
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error ? e.message : "Failed to attach to the invoice",
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (action?.kind !== "delete") return;
    try {
      await deleteAttachment.mutateAsync(action.attachment.id);
      close();
    } catch (e) {
      console.error(e);
      setActionError(e instanceof Error ? e.message : "Failed to delete");
    }
  };

  const body = isLoading ? (
    <Typography>Loading…</Typography>
  ) : !pending || pending.length === 0 ? (
    <Alert severity="success">No pending attachments.</Alert>
  ) : (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, 1fr)",
        },
      }}
    >
      {pending.map((att) => (
        <PendingAttachmentCard
          key={att.id}
          attachment={att}
          onAttachToInvoice={() =>
            setAction({ kind: "attachToInvoice", attachment: att })
          }
          onInvoiceFromPdf={() =>
            setPdfInvoiceAttachmentId(att.id as AttachmentId)
          }
          onAttachToTransaction={() =>
            setAction({ kind: "attachToTransaction", attachment: att })
          }
          onEdit={() => setAction({ kind: "edit", attachment: att })}
          onDelete={() => setAction({ kind: "delete", attachment: att })}
        />
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Home
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}
      >
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Upload attachment
        </Button>
        <Button
          variant="outlined"
          startIcon={<AccountBalanceIcon />}
          onClick={() => setImportOpenedAt(Date.now())}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Import statement
        </Button>
        <Button
          variant="outlined"
          startIcon={<ReceiptLongIcon />}
          onClick={() => setNewInvoiceOpen(true)}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          New invoice
        </Button>
        <Button
          variant="contained"
          startIcon={
            scanning ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DocumentScannerIcon />
            )
          }
          onClick={() => pdfInputRef.current?.click()}
          disabled={scanning}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {scanning ? "Reading PDF…" : "Invoice from PDF"}
        </Button>
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={handlePdfPicked}
        />
      </Stack>
      {scanError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setScanError(undefined)}
        >
          {scanError}
        </Alert>
      )}
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(undefined)}
        >
          {actionError}
        </Alert>
      )}
      <StatementInvoiceWarnings onImported={showImportOutcome} />
      <InvoiceAmountWarnings />
      <RuleHealthWarnings />
      {/* Below rule health: a dead rule writes nothing at all, which is the
          louder problem than one that dispatched all but a few. */}
      <MatchProblemWarnings />
      <ImportsNeedingRerun />
      {pendingFilter && <PendingTransactionsSummary filter={pendingFilter} />}
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Attachments that aren't yet linked to an invoice or transaction.
      </Typography>
      {body}
      <AttachmentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploadSuccess={() => setUploadOpen(false)}
      />
      <NewImportModal
        key={importOpenedAt ?? "import-closed"}
        open={importOpenedAt !== null}
        onClose={() => setImportOpenedAt(null)}
        onImported={handleImported}
      />
      {newInvoiceOpen && (
        <InvoiceFormDialog
          open
          title="New invoice"
          onClose={() => setNewInvoiceOpen(false)}
          onSave={handleCreateInvoice}
        />
      )}
      {pdfInvoiceAttachmentId && (
        <InvoiceFormDialog
          open
          title="New invoice from PDF"
          prefilledAttachmentId={pdfInvoiceAttachmentId}
          autoDetectOnOpen
          onClose={() => setPdfInvoiceAttachmentId(null)}
          onSave={handleCreateInvoice}
        />
      )}
      <EntityPickerModal<InvoiceId>
        open={action?.kind === "attachToInvoice"}
        onClose={close}
        kind="invoice"
        title="Attach to invoice"
        value={null}
        onChange={handleAttachToInvoice}
      />
      <TransactionPickerDialog
        open={action?.kind === "attachToTransaction"}
        attachmentId={
          action?.kind === "attachToTransaction"
            ? (action.attachment.id as AttachmentId)
            : null
        }
        onClose={close}
        onLinked={close}
      />
      <AttachmentManageDialog
        key={action?.kind === "edit" ? action.attachment.id : "closed"}
        open={action?.kind === "edit"}
        attachment={action?.kind === "edit" ? action.attachment : null}
        onClose={close}
      />
      <ConfirmDialog
        open={action?.kind === "delete"}
        title="Delete attachment?"
        message={
          action?.kind === "delete"
            ? `Permanently delete "${action.attachment.filename}"?`
            : ""
        }
        confirmText="Delete"
        confirmColor="error"
        onCancel={close}
        onConfirm={handleConfirmDelete}
      />
      <Snackbar
        open={importOutcome !== null}
        autoHideDuration={8000}
        onClose={() => setImportOutcome(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setImportOutcome(null)}
          sx={{ width: "100%" }}
        >
          {importOutcome}
        </Alert>
      </Snackbar>
    </Box>
  );
};
